<template>
	<div class="plugins-container">
		<div class="right-top-bar">
			<SidebarMicro ref="micro-sidebar" :items="sidebarItems" :base-path="basePath" @switched-item="switchedTab" @switched-child-item="switchedChildTab" />
		</div>
		<div class="content">
			<div class="navbar-container">
				<TopNavbar ref="top-navbar" show-micro-sidebar :sidebar-items="sidebarItems" :base-path="basePath" @switched-item="switchedTab" @switched-child-item="switchedChildTab">
					<template #content>
						<div class="row navbar-slot-content">
							<h3>{{ topBarTitle }}</h3>
							<div class="navbar-right-side"></div>
						</div>
					</template>
				</TopNavbar>
			</div>
			<div class="main-content" :class="{'no-top-padding': noTopPadding}">
				<router-view />
			</div>
		</div>
	</div>
</template>

<script>
import BusinessesSelect from "@/console-new/components/shared/BusinessesSelect.vue";
import SidebarMicro from "@/console-new/components/shared/SidebarMicro.vue";
import TopNavbar from "@/console-new/components/shared/TopNavbar.vue";

import {useLanguageStore, useNotificationStore} from "@/shared/stores";
import {useUserStore, useRoomsStore, useMarketplaceStore, useExperiencesStore} from "@/console-new/stores";
import { hasPermissionForRoute, isRouteLockedByPlan, handleLockedRoute } from "@/console-new/helpers/permissions";
import AppButtonIcon from "@/shared/components/AppButtonIcon.vue";
import AppButton from "@/shared/components/AppButton.vue";

export default {
	setup() {
		let languageStore = useLanguageStore();
		let userStore = useUserStore();
		let roomsStore = useRoomsStore();
		let marketplaceStore = useMarketplaceStore();
		const experiencesStore = useExperiencesStore();
		let notificationStore = useNotificationStore();
		return {getString: languageStore.getString, notificationStore, marketplaceStore, roomsStore, userStore, experiencesStore};
	},
	name: "PluginsContainer",
	components: {AppButtonIcon, AppButton, BusinessesSelect, SidebarMicro, TopNavbar},
	data() {
		return {
			basePath: "/plugins",
			currentTab: 0,
			currentChildTab: 0,
		}
	},
	computed: {
		noTopPadding() {
			return false;
		},
		plans() {
			if(this.userStore && this.userStore.currPlans) {
				return this.userStore.currPlans;
			}
			return {};
		},
		sidebarItems() {
			return [{
				name: this.getString('shop'),
				icon: "iconShop",
				path: "/shop",
				allowAllChildren: true,
				class: "main-button with-border",
				shown: hasPermissionForRoute('/plugins/shop')
			},{
				name: this.getString('marketplace'),
				icon: "iconMarketplace",
				path: "/marketplace",
				class: "main-button",
				shown: false
			}, {
				name: this.getString("digital_menu", [this.userStore.menuWord]),
				icon: this.userStore.currBusiness && this.userStore.currBusiness.type === "other" ? "iconMenuOther" : "iconMenu",
				locked: isRouteLockedByPlan('/plugins/menu'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/menu",
				shown: hasPermissionForRoute('/plugins/menu')
			}, {
				name: this.getString("payments_qr"),
				icon: "iconOrderPay",
				locked: isRouteLockedByPlan('/plugins/orders'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/orders",
				shown: hasPermissionForRoute('/plugins/orders'),
				children: [
					{
						name: this.getString("features"),
						icon: "iconSettingsFeatures",
						path: "/features",
					},
					{
						name: this.getString("payments"),
						icon: "iconSettingsPayments",
						path: "/payments",
					}
				]
			}, {
				name: this.getString("reservations"),
				icon: "iconReservations",
				locked: isRouteLockedByPlan('/plugins/reservations'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/reservations",
				shown: hasPermissionForRoute('/plugins/reservations'),
				children: [
					{
						name: this.getString("experiences"),
						icon: "iconExperiences",
						path: "/experiences",
					},
					{
						name: this.getString("settings"),
						icon: "iconSettings",
						path: "/settings",
					}
				]
			}, {
				name: this.userStore.deliveryWord,
				icon: "iconDelivery",
				locked: isRouteLockedByPlan('/plugins/delivery'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/delivery",
				shown: hasPermissionForRoute('/plugins/delivery')
			}, {
				name: this.userStore.takeawayWord,
				icon: this.userStore.currBusiness && this.userStore.currBusiness.type === "other" ? "iconTakeawayOther" : "iconTakeaway",
				locked: isRouteLockedByPlan('/plugins/takeaway'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/takeaway",
				shown: hasPermissionForRoute('/plugins/takeaway')
			}, {
				name: this.getString("kitchen_monitor"),
				icon: "iconKitchenMonitor",
				locked: isRouteLockedByPlan('/plugins/kitchen'),
				lockedClick: ()=>{handleLockedRoute(this.$router)},
				path: "/kitchen",
				shown: hasPermissionForRoute('/plugins/kitchen')
			}, {
				name: this.getString('referral'),
				icon: "iconPresent",
				beforeEmitFunction: ()=>{ window.location.href = URL_HTML + '/user/settings'; },
				topAuto: true,
				bgEmphasis: true,
				shown: true
			}]
		},
		currentSidebarItem() {
			return this.sidebarItems[this.currentTab];
		},
		topBarTitle() {
			let title = this.currentSidebarItem.name;
			if(this.currentSidebarItem.children && this.currentSidebarItem.children[this.currentChildTab]) {
				title += " - "+this.currentSidebarItem.children[this.currentChildTab].name;
			}
			return title;
		},
	},
	methods: {
		switchedChildTab(childIndex) {
			this.currentChildTab = childIndex;
		},
		switchedTab(index) {
			this.currentTab = index;
		},
		updateCurrentTab() {
			let currentRoute = this.$route.path;
			this.sidebarItems.forEach((item, index) => {
				if(currentRoute.endsWith(item.path)) {
					this.currentTab = index;
				} else if(item.children) {
					item.children.forEach((child, childIndex) => {
						if(currentRoute.endsWith(item.path+child.path)) {
							this.currentTab = index;
							this.currentChildTab = childIndex;
						}
					});
				}
			});
		},
	},
	mounted() {
		this.updateCurrentTab();
	}
}
</script>

<style lang="scss" scoped>
.plugins-container {
	display: flex;
	flex-direction: row;
	height: 100%;

	.right-top-bar {
		display: flex;
		flex-direction: column;
		justify-content: flex-start;
		width: 250px;
		height: 100%;
		box-shadow: 0 1px 3px var(--color-alpha-3);

		.sidebar-micro {
			@include hideScrollbar;
			overflow-y: auto;
			border-top: 1px solid var(--color-alpha-3);
		}


	}

	.content {
		width: calc(100% - 220px);
	}
	.main-content {
		width: 100%;
		height: calc(100% - 60px);

		&.no-top-padding {
			padding-top: 0;
		}
	}

	.navbar-container {
		z-index: 11;
		height: 60px;
		width: 100%;

		.navbar-slot-content {
			align-items: center;
			justify-content: space-between;
			height: 100%;
			padding: 0 10px;

			@media (max-width: 920px) {
				padding: 0 10px 0 2px;
			}
		}

	}

	.multi-select-btn {
		display: flex;
		margin-left: auto;
		margin-right: 20px;
	}

	@media (max-width: 1200px) {

		.right-top-bar {
			width: 220px;
		}

	}

	@media (max-width: 920px) {
		.navbar-container {
			width: 100%;
		}
		.right-top-bar {
			display: none;
		}
		.content {
			width: 100%;
		}
	}

	@media (max-width: 768px) {
		.main-content {
			width: 100%;
		}
	}

}
</style>